import { isUnavailableToken as coreIsUnavailableToken } from '@/utils/responseGuard'

// UNAVAILABLE_TOKENS 已删除 —— token 分类统一走 responseGuard 核(union)

function token(value) {
  return String(value == null ? '' : value).trim().toLowerCase()
}

function isUnavailableToken(value) {
  return coreIsUnavailableToken(value)
}

function isFalseFlag(value) {
  return value === false || value === 0 || ['0', 'false', 'no', 'off'].includes(token(value))
}

function isTrueFlag(value) {
  return value === true || value === 1 || ['1', 'true', 'yes', 'on'].includes(token(value))
}

function hasResponseContent(content) {
  return content &&
    typeof content === 'object' &&
    !Array.isArray(content) &&
    Object.keys(content).length > 0
}

const ENVELOPE_KEYS = ['content', 'payload', 'extra', 'page', 'data', 'body', 'meta', 'trace', 'diagnostics', 'diagnostic', 'error', 'errors', 'detail', 'details']
const PAYMENT_LIST_KEYS = ['paymentList', 'paymentChannels', 'channels', 'list', 'records', 'dataList', 'items']
const SERVER_READ_PROOF_KEYS = ['serverReadProof', 'server_read_proof', 'readProof', 'read_proof']
const TOTAL_KEYS = ['total', 'totalCount', 'total_count', 'count', 'recordsTotal', 'totalElements']
const FAILURE_STATUS_TOKENS = [
  'fail',
  'failed',
  'failure',
  'error',
  'exception',
  '-1',
  '400',
  '401',
  '403',
  '404',
  '500'
]

function responseBodies(source, depth = 0, out = []) {
  if (!source || typeof source !== 'object' || depth >= 5) return out
  ENVELOPE_KEYS.forEach(key => {
    const value = source[key]
    if (Array.isArray(value)) {
      value.forEach(item => {
        if (!hasResponseContent(item)) return
        out.push(item)
        responseBodies(item, depth + 1, out)
      })
      return
    }
    if (!hasResponseContent(value)) return
    out.push(value)
    responseBodies(value, depth + 1, out)
  })
  return out
}

export function normalizeActivityPaymentList(source, depth = 0) {
  if (Array.isArray(source)) return source
  if (!source || typeof source !== 'object' || depth >= 5) return []
  for (const key of PAYMENT_LIST_KEYS) {
    if (Array.isArray(source[key])) return source[key]
  }
  for (const key of ENVELOPE_KEYS) {
    const items = normalizeActivityPaymentList(source[key], depth + 1)
    if (items.length || (source[key] && typeof source[key] === 'object' && PAYMENT_LIST_KEYS.some(listKey => Array.isArray(source[key][listKey])))) {
      return items
    }
  }
  return []
}

function responseSources(source) {
  return [source, ...responseBodies(source)].filter(hasResponseContent)
}

function hasPositiveServerReadProof(source) {
  if (!hasResponseContent(source)) return false
  return SERVER_READ_PROOF_KEYS.some(key => isTrueFlag(source[key]) && !isUnavailableToken(source[key]))
}

function findExplicitPaymentList(source) {
  if (!hasResponseContent(source)) return null
  for (const key of PAYMENT_LIST_KEYS) {
    if (Array.isArray(source[key])) return source[key]
  }
  return null
}

function hasExplicitEmptyPaymentList(source) {
  const list = findExplicitPaymentList(source)
  return Array.isArray(list) && list.length === 0
}

function explicitTotalIsZero(source) {
  if (!hasResponseContent(source)) return false
  return TOTAL_KEYS.some(key => {
    if (source[key] === undefined || source[key] === null || source[key] === '') return false
    const value = Number(String(source[key]).replace(/,/g, ''))
    return Number.isFinite(value) && value === 0
  })
}

function hasPositiveTotal(source) {
  if (!hasResponseContent(source)) return false
  return TOTAL_KEYS.some(key => {
    if (source[key] === undefined || source[key] === null || source[key] === '') return false
    const value = Number(String(source[key]).replace(/,/g, ''))
    return Number.isFinite(value) && value > 0
  })
}

function isFailureStatus(value) {
  const normalized = token(value)
  return !!normalized && (
    FAILURE_STATUS_TOKENS.includes(normalized) ||
    normalized.includes('fail') ||
    normalized.includes('error') ||
    normalized.includes('exception')
  )
}

function hasProvenEmptyPaymentHardBlocker(sources) {
  const hardFlags = sources.flatMap(source => [
    source.readOnly,
    source.readOnlyMode,
    source.readOnlyReward,
    source.read_only_mode,
    source.read_only_reward,
    source.readOnlyAction,
    source.read_only_action,
    source.readonly,
    source.read_only,
    source.unimplemented,
    source.notImplemented,
    source.not_implemented,
    source.notSupported,
    source.not_supported,
    source.stub,
    source.fallback,
    source.fallbackUsed,
    source.fallback_used,
    source.dataUnavailable,
    source.data_unavailable,
    source.readFailure,
    source.read_failure,
    source.readUnavailable,
    source.read_unavailable,
    source.routeUnavailable,
    source.route_unavailable,
    source.technicalError,
    source.technical_error,
    source.technicalMarker,
    source.technical_marker,
    source.technicalException,
    source.technical_exception,
    source.failClosed,
    source.fail_closed,
    source.falseProof,
    source.false_proof,
    source.mock,
    source.mockMode,
    source.mock_mode,
    source.mockFallback,
    source.mock_fallback,
    source.blocked,
    source.backendBlocked,
    source.backend_blocked,
    source.backendFailure,
    source.backend_failure,
    source.paymentBackendBlocked,
    source.payment_backend_blocked,
    source.paymentStatusUnconfirmed,
    source.payment_status_unconfirmed,
    source.rewardBackendBlocked,
    source.reward_backend_blocked,
    source.writeBlocked,
    source.write_blocked,
    source.notConnected,
    source.not_connected,
    source.paymentUnavailable
  ])
  const proofFlags = sources.flatMap(source => [
    source.writeProof,
    source.write_proof,
    source.serverProof,
    source.server_proof,
    source.affectedProof,
    source.affected_proof
  ])
  if (hardFlags.some(value => isTrueFlag(value) || isUnavailableToken(value))) return true
  if (proofFlags.some(value => isFalseFlag(value) || isUnavailableToken(value))) return true
  if (sources.some(hasPositiveTotal)) return true
  if (sources.some(source => isFalseFlag(source.success))) return true
  return sources.some(source => [
    source.status,
    source.state,
    source.result,
    source.code,
    source.errorCode
  ].some(isFailureStatus))
}

export function hasProvenEmptyActivityPaymentList(source) {
  if (!hasResponseContent(source)) return false
  const sources = responseSources(source)
  return sources.some(hasPositiveServerReadProof) &&
    sources.some(hasExplicitEmptyPaymentList) &&
    sources.some(explicitTotalIsZero) &&
    !hasProvenEmptyPaymentHardBlocker(sources)
}

function hasUnavailableFlag(source) {
  const bodies = responseBodies(source)
  const flagValues = [
    source.readOnly,
    source.readOnlyMode,
    source.readOnlyReward,
    source.read_only_mode,
    source.read_only_reward,
    source.readOnlyAction,
    source.read_only_action,
    source.readonly,
    source.read_only,
    source.readOnlyQuery,
    source.read_only_query,
    source.unimplemented,
    source.notImplemented,
    source.not_implemented,
    source.notSupported,
    source.not_supported,
    source.stub,
    source.fallback,
    source.fallbackUsed,
    source.fallback_used,
    source.dataUnavailable,
    source.data_unavailable,
    source.readFailure,
    source.read_failure,
    source.readUnavailable,
    source.read_unavailable,
    source.routeUnavailable,
    source.route_unavailable,
    source.technicalError,
    source.technical_error,
    source.technicalMarker,
    source.technical_marker,
    source.technicalException,
    source.technical_exception,
    source.failClosed,
    source.fail_closed,
    source.falseProof,
    source.false_proof,
    source.mock,
    source.mockMode,
    source.mock_mode,
    source.mockFallback,
    source.mock_fallback,
    source.blocked,
    source.backendBlocked,
    source.backend_blocked,
    source.backendFailure,
    source.backend_failure,
    source.paymentBackendBlocked,
    source.payment_backend_blocked,
    source.paymentStatusUnconfirmed,
    source.payment_status_unconfirmed,
    source.rewardBackendBlocked,
    source.reward_backend_blocked,
    source.writeBlocked,
    source.write_blocked,
    source.notConnected,
    source.not_connected,
    source.paymentUnavailable,
    ...bodies.flatMap(body => [
      body.readOnly,
      body.readOnlyMode,
      body.readOnlyReward,
      body.read_only_mode,
      body.read_only_reward,
      body.readOnlyAction,
      body.read_only_action,
      body.readonly,
      body.read_only,
      body.readOnlyQuery,
      body.read_only_query,
      body.unimplemented,
      body.notImplemented,
      body.not_implemented,
      body.notSupported,
      body.not_supported,
      body.stub,
      body.fallback,
      body.fallbackUsed,
      body.fallback_used,
      body.dataUnavailable,
      body.data_unavailable,
      body.readFailure,
      body.read_failure,
      body.readUnavailable,
      body.read_unavailable,
      body.routeUnavailable,
      body.route_unavailable,
      body.technicalError,
      body.technical_error,
      body.technicalMarker,
      body.technical_marker,
      body.technicalException,
      body.technical_exception,
      body.failClosed,
      body.fail_closed,
      body.falseProof,
      body.false_proof,
      body.mock,
      body.mockMode,
      body.mock_mode,
      body.mockFallback,
      body.mock_fallback,
      body.blocked,
      body.backendBlocked,
      body.backend_blocked,
      body.backendFailure,
      body.backend_failure,
      body.paymentBackendBlocked,
      body.payment_backend_blocked,
      body.paymentStatusUnconfirmed,
      body.payment_status_unconfirmed,
      body.rewardBackendBlocked,
      body.reward_backend_blocked,
      body.writeBlocked,
      body.write_blocked,
      body.notConnected,
      body.not_connected,
      body.paymentUnavailable
    ])
  ]
  const markerValues = [
    source.status,
    source.state,
    source.result,
    source.code,
    source.errorCode,
    source.errorMessage,
    source.errorMsg,
    source.msg,
    source.message,
    source.reason,
    source.desc,
    source.description,
    source.detail,
    ...bodies.flatMap(body => [
      body.status,
      body.state,
      body.result,
      body.code,
      body.errorCode,
      body.errorMessage,
      body.errorMsg,
      body.msg,
      body.message,
      body.reason,
      body.desc,
      body.description,
      body.detail
    ])
  ]
  const falseValues = [
    source.success,
    ...bodies.map(body => body.success)
  ]
  const nestedWritable = bodies.flatMap(body => [
    body.payWritable,
    body.writable,
    body.writeEnabled
  ])
  return [source, ...bodies].some(hasNonPositiveAffectedRows) ||
    flagValues.some(value => isTrueFlag(value) || isUnavailableToken(value)) ||
    markerValues.some(isUnavailableToken) ||
    falseValues.some(isFalseFlag) ||
    nestedWritable.some(isFalseFlag)
}

function hasNonPositiveAffectedRows(source) {
  if (!source || typeof source !== 'object') return false
  return [
    source.affectedRows,
    source.affected_rows
  ].some(value => {
    if (value === undefined || value === null || value === '') return false
    if (value === false || value === 0) return true
    if (value === true || value === 1) return false
    const number = Number(value)
    if (Number.isFinite(number)) return number <= 0
    const normalized = token(value)
    return ['false', 'no', 'off', 'fail', 'failed', 'none', 'null', 'undefined', 'placeholder'].includes(normalized) ||
      isUnavailableToken(value)
  })
}

function hasSafePaymentValue(value) {
  if (value && typeof value === 'object') return false
  const normalized = token(value)
  return !!normalized &&
    !isFalseFlag(value) &&
    !isUnavailableToken(value) &&
    ![
      '0',
      'none',
      'null',
      'undefined',
      'placeholder',
      '#',
      'about:blank',
      'javascript:void(0)'
    ].includes(normalized)
}

function hasSafePaymentUrlValue(value) {
  const text = String(value == null ? '' : value).trim()
  if (!hasSafePaymentValue(text)) return false
  if (text.charAt(0) === '#') return false
  if (/^[a-z][a-z0-9+.-]*:/i.test(text) && !/^https?:\/\//i.test(text)) return false
  return true
}

function hasPositivePaymentProof(source) {
  const bodies = responseBodies(source)
  return [source, ...bodies].some(body => {
    if (!hasResponseContent(body)) return false
    const blockingProofFlags = [
      body.serverProof,
      body.server_proof,
      body.writeProof,
      body.write_proof,
      body.affectedProof,
      body.affected_proof,
      body.affectedRowsProof,
      body.affected_rows_proof,
      body.paymentProof,
      body.payment_proof
    ]
    const paymentProofFlags = [
      body.payProof,
      body.pay_proof,
      body.orderProof,
      body.order_proof,
      body.paymentUrlProof,
      body.payment_url_proof,
      body.statusProof,
      body.status_proof,
      body.serverOrderStatusProof,
      body.server_order_status_proof
    ]
    if ([...blockingProofFlags, ...paymentProofFlags].some(isFalseFlag)) return false
    if (paymentProofFlags.some(value => isTrueFlag(value) && !isUnavailableToken(value))) return true
    return paymentProofFlags.some(hasSafePaymentValue)
  })
}

function hasSafePaymentUrlTarget(source) {
  const bodies = responseBodies(source)
  return [source, ...bodies].some(body => hasResponseContent(body) && [
    body.paymentUrl,
    body.payUrl,
    body.url,
    body.cashierUrl,
    body.checkoutUrl,
    body.redirectUrl,
    body.qrcode,
    body.qrCode
  ].some(hasSafePaymentUrlValue))
}

export function isUsableActivityPaymentMethod(payment) {
  const source = payment && typeof payment === 'object' ? payment : {}
  const paymentKey = token(source.paymentKey || source.paymentId)
  const status = token(source.status || source.paymentStatus || source.channelStatus)
  const closeValues = [source.isClose, source.closed, source.close].map(token)
  const enabledValues = [source.enabled, source.enable, source.isEnabled, source.open, source.isOpen]
  if (!paymentKey || !hasSafePaymentValue(paymentKey) || isUnavailableToken(status)) return false
  if (hasUnavailableFlag(source)) return false
  if (isFalseFlag(source.payWritable) || isFalseFlag(source.writable) || isFalseFlag(source.writeEnabled)) return false
  if (['disabled', 'closed', 'off', 'offline', 'unavailable'].includes(status)) return false
  if (closeValues.some(value => ['1', 'true', 'yes'].includes(value))) return false
  return !enabledValues.some(isFalseFlag)
}

export function selectActivityPaymentMethod(paymentList) {
  if (paymentList && typeof paymentList === 'object' && !Array.isArray(paymentList) && hasUnavailableFlag(paymentList)) return null
  const items = normalizeActivityPaymentList(paymentList)
  return items.find(isUsableActivityPaymentMethod) || null
}

export function isSafeActivityPaymentSuccess(res) {
  if (!res || typeof res !== 'object' || hasUnavailableFlag(res)) return false
  const bodies = [res, ...responseBodies(res)]
  if (bodies.some(hasNonPositiveAffectedRows)) return false
  const okValues = bodies.flatMap(body => [
    body.status,
    body.state,
    body.result,
    body.code,
    body.success
  ])
  const hasOk = okValues.some(value => ['0', '00', '0000', '200', 'ok', 'success', 'succeeded', 'completed', 'true', '1'].includes(token(value)) || value === true || value === 1)
  return hasOk && hasSafePaymentUrlTarget(res) && hasPositivePaymentProof(res)
}

export function activityPaymentUnavailableMessage() {
  return 'No payment method available'
}
